import { z } from "zod";

/**
 * Zod schemas for the composite-item primitive (`IComponentGroup`).
 *
 * 🔴 THIS SCHEMA IS LOAD-BEARING FOR PERSISTENCE, not just validation.
 * `packages/client/src/services/items.ts` builds
 * `ITEM_SCHEMA_KEYS = new Set(Object.keys(ItemBaseSchema.shape))` and runs every
 * create/batch write through `validateAgainstSchema`. A field that exists on the
 * MODEL but not in `ItemBaseSchema` is stripped and the write still succeeds —
 * the Firestore-direct twin of Fastify's `removeAdditional: true`. So
 * `componentGroups` must be registered in `ItemBaseSchema` in the same change as
 * the model, or composites save as ordinary items with no visible error.
 *
 * `.strict()` matches its siblings (ModifierGroupReference, MenuGroup's nested
 * schemas): an unknown key fails loudly rather than being silently dropped.
 */

/** One selectable item inside a component group. */
export const ComponentChoiceSchema = z
  .object({
    /** The REAL menu item this choice rings up. */
    itemId: z
      .number()
      .int({ message: "Component choice itemId must be an integer" })
      .readonly(),
    /**
     * The HOME menu group of this choice — NOT the composite's group. Drives
     * course and prep-station resolution for the child line. Optional because a
     * choice whose item carries its own course/stations needs no group, but the
     * authoring UI should supply it whenever the item lives in one.
     */
    menuGroupId: z
      .number()
      .int({ message: "Component choice menuGroupId must be an integer" })
      .optional()
      .readonly(),
    /**
     * Upcharge in currency units. NON-NEGATIVE by construction: a negative
     * delta would be a discount hidden inside a menu structure, invisible to
     * every discount report and un-auditable.
     */
    priceDelta: z
      .number()
      .nonnegative({ message: "Component choice priceDelta cannot be negative" })
      .optional()
      .readonly(),
    // NO `position`. A menu item already carries its position in the menu
    // group that owns it, so a per-choice one is a second competing answer and
    // an operator reordering the menu would leave every composite holding a
    // stale order. Order is the authored `choices` array.
    //
    // `.strict()` is safe to keep: nothing PARSES a stored item through this
    // schema. The management write path is Firestore-direct and filters
    // TOP-LEVEL keys against `ItemBaseSchema` only, so a legacy document's
    // leftover `position` is never handed to this validator.
    isHidden: z.boolean().optional().readonly(),
  })
  .strict();

/** A "choose N" prompt on a composite item. */
export const ComponentGroupSchema = z
  .object({
    componentGroupId: z
      .number()
      .int({ message: "componentGroupId must be an integer" })
      .readonly(),
    name: z
      .string()
      .min(1, { message: "Component group name cannot be empty" })
      .readonly(),
    minSelection: z
      .number()
      .int()
      .min(0, { message: "minSelection cannot be negative" })
      .readonly(),
    maxSelection: z
      .number()
      .int()
      .min(1, { message: "maxSelection must be at least 1" })
      .readonly(),
    // NO `position` — same rule; the authored `componentGroups` array is the
    // order.
    /**
     * NON-EMPTY. A group with no choices is a prompt the guest cannot answer:
     * with `minSelection >= 1` it makes the composite unorderable, and with
     * `minSelection: 0` it renders an empty picker. Both are authoring mistakes
     * that should fail at save, not at the till.
     */
    choices: z
      .array(ComponentChoiceSchema)
      .min(1, { message: "A component group must have at least one choice" })
      .readonly(),
  })
  .strict()
  .refine((d) => d.maxSelection >= d.minSelection, {
    message: "maxSelection cannot be less than minSelection",
    path: ["maxSelection"],
  })
  .refine(
    (d) =>
      new Set(d.choices.map((c) => c.itemId)).size === d.choices.length,
    {
      // The same item twice in one group cannot be told apart in the picker,
      // and the two entries can carry DIFFERENT priceDeltas — so which one the
      // guest got is unanswerable after the fact.
      message: "A component group cannot list the same item twice",
      path: ["choices"],
    },
  );

export type ComponentChoiceType = z.infer<typeof ComponentChoiceSchema>;
export type ComponentGroupType = z.infer<typeof ComponentGroupSchema>;

export default ComponentGroupSchema;
